import { RemirrorJSON } from "remirror";
import {
  IEntryRecord,
  ICreatedRecordParameters,
  IGenericRequestParameters,
  IEntityMinimalModel,
  IModelWithIntId,
  IModificationRecord,
  IModifiedRecordParameters,
  IPaginationParameters,
  IPermissionedEntity,
  IVersionedEntity,
  IVersionedEntityParameters,
  IConcurrencyToken,
  INamedEntity,
  ISoftDeletable,
  ISoftDeletableFilterParameters,
  EntityConstants,
  EntryRecordOrderBy,
  ModificationRecordOrderBy,
  VersionedOrderBy,
  IProjectBasedParameters,
  ISessionedEntity,
  IUniqueEntity,
  IUniqueEntityParameters,
  IRelatedEntity,
  IRelationModel,
  IRelation,
  ILockableEntity,
  ILockableEntityParameters,
  ISignableEntity,
  ISignableEntityParameters,
  GenericEntityOrderBy,
  NamedEntityOrderBy,
} from "../../api/GenericTypes";
import { LabNotebookExperimentMinimalModel } from "./LabNotebookExperiment";
import {
  PropertyTranslator,
  IPaginationParametersTranslator,
  IGenericRequestParametersTranslator,
  IEntryRecordParametersTranslator,
  IModifiedRecordParametersTranslator,
  ISoftDeletableFilterParametersTranslator,
  IProjectBasedParametersTranslator,
  IEntryRecordLabels,
  IModificationRecordLabels,
  INamedEntityLabels,
  IPermissionedEntityLabels,
  ISoftDeletableLabels,
  IConcurrencyTokenLabels,
  ISessionedEntityLabels,
  IUniqueEntityLabels,
  IUniqueEntityParametersTranslator,
  IRelatedEntityLabels,
  ILockableEntityLabels,
  ILockableEntityParametersTranslator,
  ISignableEntityLabels,
  ISignableEntityParametersTranslator,
} from "../../api/GenericTranslator";

export const labNotebookEntriesConstants: EntityConstants<LabNotebookEntry, LabNotebookEntryFilters> = {
  resource: "lab_notebook_entries",
  frontendIndexRoute: "lab_notebook_entries",
  entitySingular: "notebook entry",
  entityPlural: "notebook entries",
  icon: "pencil",
};

export const labNotebookEntryFieldLabels: PropertyTranslator<LabNotebookEntry> = {
  ...INamedEntityLabels,
  ...IPermissionedEntityLabels,
  ...IEntryRecordLabels,
  ...IModificationRecordLabels,
  ...ISoftDeletableLabels,
  ...IConcurrencyTokenLabels,
  ...ISessionedEntityLabels,
  ...IUniqueEntityLabels,
  ...IRelatedEntityLabels,
  ...ILockableEntityLabels,
  ...ISignableEntityLabels,
  content: "Content",
  id: "Notebook entry ID",
  version: "Version",
  entryDate: "Entry date",
  labNotebook: "Notebook",
  labNotebookExperiment: "Experiment",
  alias: "Name",
};

export const EmptyLabNotebookEntry = { type: "doc", content: [] };

export interface LabNotebookMinimalModel extends IEntityMinimalModel {}
export interface LabNotebookEntry
  extends IModelWithIntId,
    INamedEntity,
    IEntryRecord,
    IModificationRecord,
    IPermissionedEntity,
    IVersionedEntity,
    ISoftDeletable,
    IConcurrencyToken<string>,
    ISessionedEntity,
    IUniqueEntity,
    IRelatedEntity<LabNotebookEntryRelations>,
    ILockableEntity,
    ISignableEntity {
  labNotebook: LabNotebookMinimalModel;
  labNotebookExperiment: LabNotebookExperimentMinimalModel;
  entryDate: string;
  content?: RemirrorJSON;
  alias?: string;
}

export interface LabNotebookEntryRelations extends IRelationModel {
  datasets: IRelation;
  attachments: IRelation;
  labNotebookEntries: IRelation;
  labNotebookExperiments: IRelation;
  labNotebooks: IRelation;
  persons: IRelation;
  samples: IRelation;
  labNotebookTemplates: IRelation;
  projects: IRelation;
  inventoryItems: IRelation;
}

export interface LabNotebookEntrySuggestions extends IEntityMinimalModel<LabNotebookEntry["id"]> {
  entryDate: string;
  alias?: string;
  labNotebook: LabNotebookMinimalModel;
  labNotebookExperiment: LabNotebookExperimentMinimalModel;
}

export interface LabNotebookEntryVersion extends LabNotebookEntry, IVersionedEntity {}

export interface LabNotebookEntryWriteModel
  extends Omit<
      LabNotebookEntry,
      | "id"
      | "name"
      | "labNotebook"
      | "enteredBy"
      | "enteredOn"
      | "modifiedBy"
      | "modifiedOn"
      | "isDeleted"
      | "originalId"
      | "version"
    >,
    IConcurrencyToken<string> {}

export interface LabNotebookEntryFilters
  extends IGenericRequestParameters<LabNotebookEntry, LabNotebookEntrySortingOptions>,
    IPaginationParameters,
    ICreatedRecordParameters,
    IModifiedRecordParameters,
    ISoftDeletableFilterParameters,
    IProjectBasedParameters,
    IUniqueEntityParameters,
    ILockableEntityParameters,
    ISignableEntityParameters {
  labNotebookReferenceIds?: number[] | null;
  labNotebookExperimentReferenceIds?: number[] | null;
  datasetReferenceIds?: number[];
  attachmentReferenceIds?: number[];
  sampleReferenceIds?: number[];
  inventoryItemReferenceIds?: number[];
  personReferenceIds?: number[];
  entryDateFrom?: string | null;
  entryDateTo?: string | null;
  includeContent?: boolean;
  useFullTextSearch?: boolean | null;
  labNotebookProjectIds?: number[] | null;
  projectReferenceIds?: number[] | null;
}

export interface LabNotebookEntryVersionFilter extends LabNotebookEntryFilters, IVersionedEntityParameters {}

export const LabNotebookEntrySortingOptionsConsts = [
  ...GenericEntityOrderBy,
  ...NamedEntityOrderBy,
  ...EntryRecordOrderBy,
  ...ModificationRecordOrderBy,
  ...VersionedOrderBy,
  "ENTRY_DATE",
  "LAB_NOTEBOOK_ID",
  "LAB_NOTEBOOK_NAME",
  "LAB_NOTEBOOK_EXPERIMENT_ID",
  "LAB_NOTEBOOK_EXPERIMENT_NAME",
] as const;
export type LabNotebookEntrySortingOptions = (typeof LabNotebookEntrySortingOptionsConsts)[number];

export const LabNotebookEntryFiltersTranslator: PropertyTranslator<LabNotebookEntryFilters> = {
  ...IPaginationParametersTranslator,
  ...IGenericRequestParametersTranslator,
  ...IEntryRecordParametersTranslator,
  ...IModifiedRecordParametersTranslator,
  ...ISoftDeletableFilterParametersTranslator,
  ...IProjectBasedParametersTranslator,
  ...IUniqueEntityParametersTranslator,
  ...ILockableEntityParametersTranslator,
  ...ISignableEntityParametersTranslator,
  labNotebookReferenceIds: "Notebook IDs",
  labNotebookExperimentReferenceIds: "Experiment IDs",
  datasetReferenceIds: "Dataset IDs",
  inventoryItemReferenceIds: "Inventory IDs",
  attachmentReferenceIds: "Attachment IDs",
  sampleReferenceIds: "Sample IDs",
  personReferenceIds: "Person IDs",
  entryDateFrom: "Entry date from",
  entryDateTo: "Entry date to",
  includeContent: "Include content",
  useFullTextSearch: "Full text search",
  labNotebookProjectIds: "Notebook Project IDs",
  projectReferenceIds: "Project IDs",
};
